import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface BridgePayTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  issued_at: string;
}

export interface BridgePayApiKeyData {
  api_key: string;
  tenant_id: string;
}

@Injectable({
  providedIn: 'root',
})
export class BridgePayAuthService {
  private readonly TOKEN_KEY = 'bridgepay_token';
  private readonly TOKEN_EXPIRY_KEY = 'bridgepay_token_expiry';
  private readonly API_KEY_KEY = 'bridgepay_api_key';
  private readonly TENANT_ID_KEY = 'bridgepay_tenant_id';

  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();

  private baseUrl = `${environment.BRIDGEPAY_BASE}`;
  private refreshTimer: any;

  constructor(private http: HttpClient) {
    // Load token from storage on init
    this.loadStoredToken();
  }

  /**
   * Initialize BridgePay authentication with API key
   * This should be called when the user logs in or when the organization is selected
   */
  public initializeAuth(apiKey: string, tenantId: string): Observable<string> {
    // Store API key and tenant ID for future token refreshes
    localStorage.setItem(this.API_KEY_KEY, apiKey);
    localStorage.setItem(this.TENANT_ID_KEY, tenantId);

    return this.exchangeApiKeyForToken(apiKey, tenantId);
  }

  /**
   * Exchange API key for JWT token
   */
  private exchangeApiKeyForToken(apiKey: string, tenantId: string): Observable<string> {
    const payload = {
      api_key: apiKey,
      tenant_id: tenantId,
    };

    return this.http.post<BridgePayTokenResponse>(`${this.baseUrl}/v1/auth/token`, payload).pipe(
      tap((response) => {
        this.storeToken(response);
        this.scheduleTokenRefresh(response.expires_in);
      }),
      switchMap((response) => {
        return of(response.access_token);
      }),
      catchError((error) => {
        this.clearAuth();
        return throwError(() => error);
      }),
    );
  }

  /**
   * Get current token, refreshing if necessary
   */
  public getToken(): Observable<string | null> {
    const token = this.tokenSubject.value;
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      // Try to refresh if we have stored credentials
      const apiKey = localStorage.getItem(this.API_KEY_KEY);
      const tenantId = localStorage.getItem(this.TENANT_ID_KEY);

      if (apiKey && tenantId) {
        return this.exchangeApiKeyForToken(apiKey, tenantId);
      }
      return of(null);
    }

    const expiryTime = new Date(expiry).getTime();
    const now = new Date().getTime();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

    if (now >= expiryTime - bufferTime) {
      // Token is expired or about to expire, refresh it
      return this.refreshToken();
    }

    return of(token);
  }

  /**
   * Refresh the current token
   */
  private refreshToken(): Observable<string | null> {
    const apiKey = localStorage.getItem(this.API_KEY_KEY);
    const tenantId = localStorage.getItem(this.TENANT_ID_KEY);

    if (!apiKey || !tenantId) {
      this.clearAuth();
      return of(null);
    }

    return this.exchangeApiKeyForToken(apiKey, tenantId);
  }

  /**
   * Store token and expiry information
   */
  private storeToken(response: BridgePayTokenResponse): void {
    const token = response.access_token;
    const expiresIn = response.expires_in || 3600; // Default to 1 hour
    const expiryTime = new Date(Date.now() + expiresIn * 1000);

    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toISOString());

    this.tokenSubject.next(token);
  }

  /**
   * Schedule automatic token refresh before expiry
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = (expiresIn - 300) * 1000; // 5 minutes before expiry
    if (refreshTime > 0) {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken().subscribe();
      }, refreshTime);
    }
  }

  /**
   * Load stored token on service initialization
   */
  private loadStoredToken(): void {
    const token = localStorage.getItem(this.TOKEN_KEY);
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (token && expiry) {
      const expiryTime = new Date(expiry).getTime();
      const now = new Date().getTime();

      if (now < expiryTime) {
        this.tokenSubject.next(token);

        // Schedule refresh for this session
        const remainingTime = Math.floor((expiryTime - now) / 1000);
        this.scheduleTokenRefresh(remainingTime);
      } else {
        // Token is expired, try to refresh
        this.refreshToken().subscribe();
      }
    }
  }

  /**
   * Check if BridgePay auth is initialized
   */
  public isAuthenticated(): boolean {
    return !!this.tokenSubject.value;
  }

  /**
   * Clear all authentication data
   */
  public clearAuth(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.API_KEY_KEY);
    localStorage.removeItem(this.TENANT_ID_KEY);

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    this.tokenSubject.next(null);
  }

  /**
   * Update stored API key (for when organization generates new keys)
   */
  public updateApiKey(apiKey: string, tenantId: string): Observable<string> {
    this.clearAuth();
    return this.initializeAuth(apiKey, tenantId);
  }
}
